/**
 * Sub-components used by EstateVaultPanel.js. Moved out only to keep
 * EstateVaultPanel.js under the 200-line function-length budget — no
 * behaviour change.
 */
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  ShieldCheck, Pause, Play, RotateCcw, AlertTriangle, FileLock2, Download,
  RefreshCw, Users, Plus, Trash2, ChevronUp, ChevronDown, CheckCircle2, Eye,
  PenLine,
} from 'lucide-react';
import { TrustedContactEditModal } from './estate-vault/TrustedContactEditModal';

/** Small amber "pen + Add {label}" nudge chip used inside a Trusted
 *  Contact row for each missing optional field (phone/address/notes). */
function TrustedContactAddNudge({ label, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100 transition"
    >
      <PenLine className="w-3 h-3" />
      <span>Add {label}</span>
    </button>
  );
}

/** For a Trusted Contact, return the list of nudge-able optional fields
 *  that are still empty. Currently: phone, address, relationship_notes. */
const TC_OPTIONAL_FIELDS = [
  { key: 'phone', label: 'phone' },
  { key: 'address', label: 'address' },
  { key: 'relationship_notes', label: 'notes' },
];
const missingContactFields = (c) =>
  TC_OPTIONAL_FIELDS.filter((f) => !c?.[f.key] || !String(c[f.key]).trim());

export function ActivationGate({ state, consents, setConsents, submitting, handleActivate }) {
  const isTier1 = !!state.owner_country_is_tier1 || state.exists;
  const isReactivation = state.exists && state.status === 'revoked';
  return (
    <div className="bg-white rounded-xl shadow-xs border border-slate-200 p-4 sm:p-6" data-testid="estate-vault-panel">
      <div className="flex items-start gap-3 mb-4">
        <ShieldCheck className="w-6 h-6 text-emerald-700 mt-0.5" />
        <div>
          <h3 className="text-lg sm:text-xl font-bold text-slate-900">
            {isReactivation ? 'Reactivate Continuity System' : 'Continuity System'}
          </h3>
          <p className="text-sm text-slate-700 mt-2 max-w-2xl leading-relaxed">
            {isReactivation ? (
              <>
                Welcome back. Reactivating starts your Continuity System with a <strong>fresh slate</strong> &mdash;
                you&rsquo;ll re-add your Trusted Contacts and generate new Continuity Records. Nothing from the
                previous vault is restored.
              </>
            ) : (
              <>
                Continuity System is a quiet way to make sure the people you trust most can <strong>find</strong> your
                investments when you&rsquo;re not available. It is <strong>not</strong> a will, trust, or probate
                service &mdash; it simply hands over a snapshot of where your holdings live, so nothing
                gets lost or forgotten.
              </>
            )}
          </p>
        </div>
      </div>
      {!isTier1 && (
        <div className="border border-amber-200 bg-amber-50 rounded-lg p-3 text-sm text-amber-900 mb-4" data-testid="estate-vault-tier1-blocked">
          Continuity System is not available in your jurisdiction yet. We will let you know once it is.
        </div>
      )}
      {isTier1 && (
        <div className="space-y-4 max-w-2xl">
          <div className="border border-emerald-100 bg-emerald-50/50 rounded-lg p-3 text-sm text-slate-700">
            <p className="font-medium text-slate-900 mb-1">How it works, in plain terms</p>
            <ol className="list-decimal pl-5 space-y-1 text-sm">
              <li>You name up to {state.max_successors} <strong>Trusted Contacts</strong> (people you trust to receive the information).</li>
              <li>You generate your <strong>Continuity Records</strong> any time you want &mdash; we keep the latest version encrypted, ready to share.</li>
              <li>Every {Math.round((state.checkin_interval_days || 90) / 30)} months we send you a short check-in. Just click &ldquo;I&rsquo;m well&rdquo; and nothing happens.</li>
              <li>If you stop responding for several check-ins in a row, our admin team verifies the situation and only then shares the summary with the people you named. Two staff members must approve before anything is released.</li>
            </ol>
          </div>
          <p className="text-sm text-slate-600">
            You can <strong>pause</strong> or <strong>permanently revoke</strong> Continuity System at any time &mdash; you stay in full control.
            To activate, please confirm the three points below.
          </p>
          <div className="space-y-3 border border-slate-200 rounded-lg p-4 bg-slate-50">
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <Checkbox checked={consents.nature} onCheckedChange={(v) => setConsents({ ...consents, nature: !!v })} data-testid="ev-consent-nature" />
              <span>I understand MyInvestFolio Continuity System is an information-transfer mechanism only. It is not a will, trust, probate service, or legal instrument, and does not transfer ownership of any asset.</span>
            </label>
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <Checkbox checked={consents.ndpa} onCheckedChange={(v) => setConsents({ ...consents, ndpa: !!v })} data-testid="ev-consent-ndpa" />
              <span>I consent to MyInvestFolio processing my Trusted Contacts&apos; personal data strictly for the purpose of identification and information transfer, in line with the Privacy Policy.</span>
            </label>
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <Checkbox checked={consents.ack} onCheckedChange={(v) => setConsents({ ...consents, ack: !!v })} data-testid="ev-consent-ack" />
              <span>I confirm I have informed each Trusted Contact of their designation and that they consent to being contacted.</span>
            </label>
          </div>
          <Button onClick={handleActivate} disabled={submitting || !(consents.nature && consents.ndpa && consents.ack)}
            className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="ev-activate-btn">
            <ShieldCheck className="w-4 h-4 mr-2" /> {isReactivation ? 'Reactivate Continuity System' : 'Activate Continuity System'}
          </Button>
        </div>
      )}
    </div>
  );
}

const STATUS_COLOR = {
  active: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  paused: 'bg-amber-100 text-amber-800 border-amber-300',
  triggered: 'bg-rose-100 text-rose-800 border-rose-300',
  released: 'bg-slate-100 text-slate-800 border-slate-300',
  revoked: 'bg-slate-100 text-slate-500 border-slate-300',
};

const CHECKIN_STAGE_TONE = (stage) => {
  if (stage >= 4) return 'bg-rose-100 text-rose-800 border-rose-300';
  if (stage === 3) return 'bg-orange-100 text-orange-800 border-orange-300';
  return 'bg-amber-100 text-amber-800 border-amber-300';
};
const CHECKIN_STAGE_TIP = (stage, total) => {
  const remaining = Math.max(0, (total || 5) - stage);
  if (stage >= 4) return `Urgent: only ${remaining} step${remaining === 1 ? '' : 's'} remain before we start approaching your Trusted Contacts. Tap "I'm OK" to reset the clock.`;
  if (stage === 3) return `Concerning: ${remaining} steps remain before we start approaching your Trusted Contacts. Tap "I'm OK" to reset the clock.`;
  return `Friendly nudge — we haven't heard from you in about 30 days. ${remaining} steps remain before we would approach your Trusted Contacts. Tap "I'm OK" to reset the clock.`;
};

export function VaultHeader({ state, status, handlePause, handleResume, handleCheckinRespond, loadAudit, handleRevoke }) {
  const overdue = (state.dms_stage || 0) > 0;
  const stage = state.dms_stage || 0;
  const total = state.dms_total_stages || 5;
  return (
    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
      <div className="flex items-start gap-3">
        <ShieldCheck className="w-6 h-6 text-emerald-700 mt-0.5" />
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-lg sm:text-xl font-bold text-slate-900">Welcome to Continuity System</h3>
            <Badge className={`border ${STATUS_COLOR[status] || ''}`} data-testid="ev-status-badge">{status}</Badge>
            {overdue && (
              <Badge
                className={`border ${CHECKIN_STAGE_TONE(stage)}`}
                title={CHECKIN_STAGE_TIP(stage, total)}
                data-testid="ev-checkin-overdue-badge"
              >
                Check-in overdue — Step {stage} of {total}
              </Badge>
            )}
          </div>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Quietly making sure the right people can <strong>find</strong> your investments when you&rsquo;re not available. Not a will or trust &mdash; just secure information discovery and access, showing where your holdings live.
          </p>
        </div>
      </div>
      <div className="flex gap-2 flex-wrap">
        {overdue && (
          <Button
            size="sm" onClick={handleCheckinRespond}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            title="Confirm you&rsquo;re well. This resets your check-in clock and stops the reminder cascade."
            data-testid="ev-im-ok-btn"
          ><CheckCircle2 className="w-4 h-4 mr-1.5" /> I&rsquo;m OK</Button>
        )}
        {status === 'active' && (
          <Button
            variant="outline" size="sm" onClick={handlePause}
            title="Temporarily stop check-in emails. Nothing is deleted; you can resume any time."
            data-testid="ev-pause-btn"
          ><Pause className="w-4 h-4 mr-1.5" /> Pause</Button>
        )}
        {status === 'paused' && (
          <Button
            variant="outline" size="sm" onClick={handleResume}
            title="Resume check-in emails. Your next check-in cycle restarts today."
            data-testid="ev-resume-btn"
          ><Play className="w-4 h-4 mr-1.5" /> Resume</Button>
        )}
        <Button
          variant="outline" size="sm" onClick={loadAudit}
          title="See every action you or our team has taken on your Continuity System."
          data-testid="ev-audit-btn"
        ><RotateCcw className="w-4 h-4 mr-1.5" /> Activity log</Button>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              variant="outline" size="sm" className="text-rose-700 hover:text-rose-800"
              title="Permanently turn off Continuity System. Your Trusted Contacts will not receive anything. You can set it up again later."
              data-testid="ev-revoke-btn"
            ><AlertTriangle className="w-4 h-4 mr-1.5" /> Turn off</Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Turn off Continuity System?</AlertDialogTitle>
              <AlertDialogDescription>
                Your Continuity Records will be invalidated and your Trusted Contact designations will be removed. The people you named will no longer receive anything. You can set everything up again later if you change your mind. This action is recorded in your activity log.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleRevoke} className="bg-rose-600 hover:bg-rose-700" data-testid="ev-revoke-confirm">Turn off Continuity System</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}

export function VaultSummaryPanel({ snapshots, handlePreview, handleGenerateSnapshot, submitting, canEdit, hasVaultContent = true }) {
  // React hooks must run in the same order every render, so declare state
  // BEFORE any early return.
  const [historyOpen, setHistoryOpen] = React.useState(false);

  // Hide entirely if the vault has no content to summarise yet (Continuity-first
  // user who just activated and hasn't added a Trusted Contact or a Vault Asset).
  if (!hasVaultContent) return null;

  const hasSnapshots = snapshots.length > 0;
  return (
    <div className="border border-slate-200 rounded-lg p-4 bg-slate-50">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h4 className="font-semibold text-slate-800 flex items-center gap-2"><FileLock2 className="w-4 h-4 text-emerald-700" /> Continuity Records</h4>
          <p className="text-xs text-slate-500 mt-1 max-w-xl">
            A single encrypted PDF listing your Trusted Contacts and the assets you&rsquo;ve catalogued in your Continuity System &mdash; the document a verified Trusted Contact would receive. Only you can view it. Refresh it whenever anything in your Continuity System changes.
            {hasSnapshots && (<> Last refreshed <strong>{new Date(snapshots[0].generated_at).toLocaleString()}</strong>.</>)}
          </p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={handlePreview} disabled={!hasSnapshots} data-testid="ev-preview-btn"><Download className="w-4 h-4 mr-1.5" /> View my Continuity Records</Button>
          <Button size="sm" onClick={handleGenerateSnapshot} disabled={submitting || !canEdit} className="bg-emerald-700 hover:bg-emerald-800" data-testid="ev-generate-btn"><RefreshCw className="w-4 h-4 mr-1.5" /> Refresh Continuity Records</Button>
        </div>
      </div>
      {hasSnapshots && (
        <div className="mt-3 text-xs text-slate-600">
          {/* Current version — the only line that day-to-day matters. */}
          <div className="flex items-center gap-2" data-testid="ev-snapshot-current">
            <span className="inline-flex h-1.5 w-1.5 rounded-full bg-emerald-600" />
            <span>{new Date(snapshots[0].generated_at).toLocaleString()} &middot; {(snapshots[0].byte_size / 1024).toFixed(1)} KB &middot; {snapshots[0].investment_count} entries</span>
            <Badge className="border bg-emerald-100 text-emerald-800 border-emerald-300 text-[10px] px-1.5 py-0">current</Badge>
          </div>
          {/* Earlier versions — collapsed by default so the panel stays quiet
              day-to-day. A single toggle reveals them for the rare audit case. */}
          {snapshots.length > 1 && (
            <div className="mt-2">
              <button
                type="button"
                onClick={() => setHistoryOpen((v) => !v)}
                className="text-[11px] text-emerald-700 hover:text-emerald-900 hover:underline"
                data-testid="ev-snapshot-toggle-history"
                aria-expanded={historyOpen}
              >
                {historyOpen ? 'Hide' : 'See'} earlier versions ({snapshots.length - 1})
              </button>
              {historyOpen && (
                <ul className="space-y-1 mt-1" data-testid="ev-snapshot-list">
                  {snapshots.slice(1, 6).map((s) => (
                    <li key={s.snapshot_id} className="flex items-center gap-2">
                      <span className="inline-flex h-1.5 w-1.5 rounded-full bg-slate-400" />
                      <span>{new Date(s.generated_at).toLocaleString()} &middot; {(s.byte_size / 1024).toFixed(1)} KB &middot; {s.investment_count} entries</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Back-compat alias — older imports may still reference the old name.
export const PortfolioSummaryPanel = VaultSummaryPanel;

const TRUSTED_CONTACT_ROLES = ['Information Only', 'Lawyer', 'Coordinator', 'Other'];

function displayName(s) {
  const composed = `${s.first_name || ''} ${s.last_name || ''}`.trim();
  return composed || s.full_name || '(no name)';
}

export function TrustedContactsPanel({ state, canEdit, handleRemoveSuccessor, handlePreviewSuccessor, handleAddSuccessor, handleUpdateTrustedContact, newSucc, setNewSucc, submitting }) {
  const minContacts = state.min_successors || state.max_successors || 3;
  const quorumRequired = state.quorum_required || 2;
  const [editing, setEditing] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const openEditor = (contact) => { setEditing(contact); setEditorOpen(true); };
  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2 gap-1">
        <h4 className="font-semibold text-slate-800 flex items-center gap-2"><Users className="w-4 h-4 text-emerald-700" /> Trusted Contacts</h4>
        <span className="text-xs text-slate-500" data-testid="ev-contact-count">
          {state.successor_count} of {state.max_successors} added
        </span>
      </div>
      <p className="text-xs text-slate-500 mb-3 max-w-2xl" data-testid="ev-tc-helper">
        Choose {minContacts} people you trust. Any {quorumRequired} of them will need to confirm before your record is released &mdash; they act as peers, not in any ranked order. You can swap any of them out at any time. This is information access only, not an inheritance or a legal instruction to transfer any asset.
      </p>
      {state.low_contact_warning && (
        <div
          className={
            'mb-3 rounded-lg px-3 py-2 text-sm border ' + (
              state.low_contact_warning.level === 'red' ? 'border-rose-400 bg-rose-50 text-rose-800' :
              state.low_contact_warning.level === 'yellow' ? 'border-amber-400 bg-amber-50 text-amber-800' :
              'border-slate-300 bg-slate-50 text-slate-700'
            )
          }
          data-testid={`ev-low-contact-banner-${state.low_contact_warning.level}`}
        >
          <div className="flex items-start gap-2">
            <AlertTriangle className={
              'w-4 h-4 mt-0.5 shrink-0 ' + (
                state.low_contact_warning.level === 'red' ? 'text-rose-700' :
                state.low_contact_warning.level === 'yellow' ? 'text-amber-700' : 'text-slate-500'
              )
            } />
            <div>
              <span className="font-medium">
                {state.low_contact_warning.contacts_needed >= 2
                  ? `Add ${state.low_contact_warning.contacts_needed} more Trusted Contacts.`
                  : 'Add 1 more Trusted Contact.'}
              </span>{' '}
              With fewer than {minContacts}, the {quorumRequired}-of-{minContacts} confirmation
              needed to release your record can never be met.{' '}
              <span className="opacity-80">({state.low_contact_warning.days} days below {minContacts})</span>
            </div>
          </div>
        </div>
      )}
      {state.successors.length === 0 ? (
        <p className="text-sm text-slate-500 mb-3 italic">No one added yet. Use the form below to add your first Trusted Contact.</p>
      ) : (
        <div className="space-y-2 mb-4">
          {state.successors.map((s) => {
            const missing = canEdit && handleUpdateTrustedContact ? missingContactFields(s) : [];
            return (
            <div key={s.successor_id} className="border border-slate-200 rounded-lg px-3 py-2 bg-white" data-testid={`ev-tc-${s.successor_id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="text-sm flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge
                      variant="outline"
                      className="border-slate-300 text-slate-700 capitalize"
                      data-testid={`ev-tc-relationship-${s.successor_id}`}
                    >
                      {s.relationship || 'contact'}
                    </Badge>
                    <span className="font-medium text-slate-800">{displayName(s)}</span>
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {s.email} &middot; {s.country}
                    {s.role && <> &middot; <span className="text-slate-700">Role: {s.role === 'Other' && s.role_other ? s.role_other : s.role}</span></>}
                    &middot; status: {s.status}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {handlePreviewSuccessor && (
                    <Button
                      size="sm" variant="outline"
                      className="text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                      onClick={() => handlePreviewSuccessor(s.successor_id)}
                      data-testid={`ev-tc-preview-${s.successor_id}`}
                      title="See exactly what this Trusted Contact will experience"
                    >
                      <Eye className="w-3.5 h-3.5 mr-1" /> Preview
                    </Button>
                  )}
                  {canEdit && (
                    <Button size="sm" variant="outline" className="text-rose-700" onClick={() => handleRemoveSuccessor(s.successor_id)} data-testid={`ev-tc-remove-${s.successor_id}`}>
                      <Trash2 className="w-3.5 h-3.5 mr-1" /> Remove
                    </Button>
                  )}
                </div>
              </div>
              {missing.length > 0 && (
                <div
                  className="mt-2 pt-2 border-t border-amber-100 flex flex-wrap items-center gap-1.5"
                  data-testid={`ev-tc-nudge-row-${s.successor_id}`}
                >
                  <span className="text-[11px] text-amber-800 font-medium mr-1">Still to add:</span>
                  {missing.map((f) => (
                    <TrustedContactAddNudge
                      key={f.key}
                      label={f.label}
                      onClick={() => openEditor(s)}
                      testid={`ev-tc-nudge-${s.successor_id}-${f.key}`}
                    />
                  ))}
                </div>
              )}
            </div>
          );
          })}
        </div>
      )}
      {canEdit && state.successor_count < state.max_successors && (
        <form onSubmit={handleAddSuccessor} className="grid sm:grid-cols-2 gap-3 border border-slate-200 rounded-lg p-4 bg-white" data-testid="ev-tc-add-form">
          <div className="sm:col-span-2">
            <p className="text-xs font-semibold text-slate-700 mb-0.5" data-testid="ev-tc-add-heading">Add Trusted Contact</p>
            <p className="text-xs text-slate-500">Their country must be on our currently supported list. If it isn&rsquo;t, you&rsquo;ll see a notice when you submit.</p>
          </div>
          <div><Label htmlFor="ev-first-name">First name</Label><Input id="ev-first-name" value={newSucc.first_name} onChange={(e) => setNewSucc({ ...newSucc, first_name: e.target.value })} required data-testid="ev-first-name" /></div>
          <div><Label htmlFor="ev-last-name">Last name</Label><Input id="ev-last-name" value={newSucc.last_name} onChange={(e) => setNewSucc({ ...newSucc, last_name: e.target.value })} required data-testid="ev-last-name" /></div>
          <div><Label htmlFor="ev-email">Their email</Label><Input id="ev-email" type="email" value={newSucc.email} onChange={(e) => setNewSucc({ ...newSucc, email: e.target.value })} required data-testid="ev-email" /></div>
          <div><Label htmlFor="ev-country">Their country</Label><Input id="ev-country" placeholder="e.g. United Kingdom" value={newSucc.country} onChange={(e) => setNewSucc({ ...newSucc, country: e.target.value })} required data-testid="ev-country" /></div>
          <div><Label htmlFor="ev-relationship">Relationship to you</Label><Input id="ev-relationship" placeholder="e.g. spouse, son, daughter, lawyer, accountant" value={newSucc.relationship} onChange={(e) => setNewSucc({ ...newSucc, relationship: e.target.value })} required data-testid="ev-relationship" /></div>
          <div>
            <Label htmlFor="ev-role">Their role</Label>
            <select
              id="ev-role"
              className="flex h-9 w-full rounded-md border border-slate-200 bg-white px-3 py-1 text-sm shadow-xs"
              value={newSucc.role || 'Information Only'}
              onChange={(e) => setNewSucc({ ...newSucc, role: e.target.value })}
              data-testid="ev-role"
            >
              {TRUSTED_CONTACT_ROLES.map((r) => (<option key={r} value={r}>{r}</option>))}
            </select>
          </div>
          {newSucc.role === 'Other' && (
            <div className="sm:col-span-2">
              <Label htmlFor="ev-role-other">Describe their role</Label>
              <Input id="ev-role-other" value={newSucc.role_other || ''} onChange={(e) => setNewSucc({ ...newSucc, role_other: e.target.value })} placeholder="e.g. Family accountant" data-testid="ev-role-other" />
            </div>
          )}
          <div><Label htmlFor="ev-phone">Their phone (optional)</Label><Input id="ev-phone" value={newSucc.phone} onChange={(e) => setNewSucc({ ...newSucc, phone: e.target.value })} data-testid="ev-phone" /></div>
          <div className="sm:col-span-2"><Label htmlFor="ev-address">Their address (optional)</Label><Input id="ev-address" value={newSucc.address} onChange={(e) => setNewSucc({ ...newSucc, address: e.target.value })} data-testid="ev-address" /></div>
          <div className="sm:col-span-2"><Label htmlFor="ev-notes">Anything our admin team should know (optional)</Label><Textarea id="ev-notes" rows={2} placeholder="e.g. preferred phone hours, language, or how they know you" value={newSucc.relationship_notes} onChange={(e) => setNewSucc({ ...newSucc, relationship_notes: e.target.value })} data-testid="ev-notes" /></div>
          <div className="sm:col-span-2"><Button type="submit" disabled={submitting} className="bg-emerald-700 hover:bg-emerald-800" data-testid="ev-tc-add-submit"><Plus className="w-4 h-4 mr-1.5" /> Add this Trusted Contact</Button></div>
        </form>
      )}
      {handleUpdateTrustedContact && (
        <TrustedContactEditModal
          contact={editing}
          open={editorOpen}
          onOpenChange={setEditorOpen}
          onSave={handleUpdateTrustedContact}
        />
      )}
    </div>
  );
}

// Back-compat alias — some imports still reference the old name.
export const SuccessorsPanel = TrustedContactsPanel;
